import React, { useState, useRef, useEffect } from "react";

interface CheckboxDropdownOption {
  value: string;
  label: string;
}

interface CheckboxDropdownProps {
  title: string;
  options: CheckboxDropdownOption[];
  selected: string[] | null;
  onChange: (value: string[] | null) => void;
  /**
   * RP-2540: "pick" behaviour for long lists. From "All", clicking an option
   * selects just that option (instead of every option but it); clicking more
   * adds them; unticking the last one goes back to All (onChange(null)).
   * Off by default, so existing filters behave exactly as before.
   */
  pickMode?: boolean;
  /** Show a search box when the list is longer than this many options. */
  searchAbove?: number;
}

const CheckboxDropdown: React.FC<CheckboxDropdownProps> = ({
  title,
  options,
  selected,
  onChange,
  pickMode = false,
  searchAbove,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const allValues = options.map((o) => o.value);
  const allSelected = !selected || selected.length === 0 || selected.length === options.length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const showSearch = searchAbove !== undefined && options.length > searchAbove;
  const q = query.trim().toLowerCase();
  const shownOptions = q
    ? options.filter((o) => o.label.toLowerCase().includes(q))
    : options;

  const isChecked = (value: string) =>
    pickMode
      ? !allSelected && (selected?.includes(value) ?? false)
      : allSelected || (selected?.includes(value) ?? false);

  const handleAllToggle = () => {
    // "All" always selects every option
    onChange(pickMode ? null : allValues);
  };

  const handleToggle = (value: string) => {
    if (pickMode) {
      if (allSelected) {
        onChange([value]);
        return;
      }
      if (selected!.includes(value)) {
        const next = selected!.filter((v) => v !== value);
        onChange(next.length ? next : null);
      } else {
        onChange([...selected!, value]);
      }
      return;
    }
    if (allSelected) {
      // Unchecking one item from "All" — select everything except this one
      onChange(allValues.filter((v) => v !== value));
      return;
    }
    if (selected!.includes(value)) {
      // Don't allow deselecting the last remaining option
      if (selected!.length === 1) return;
      onChange(selected!.filter((v) => v !== value));
    } else {
      const next = [...selected!, value];
      onChange(next);
    }
  };

  const displayText = allSelected
    ? "All"
    : selected!.length === 1
      ? options.find((o) => o.value === selected![0])?.label ?? selected![0]
      : `${selected!.length} of ${options.length} selected`;

  return (
    <div className="w-full" ref={ref}>
      <label className="block text-sm font-medium text-gray-700">{title}</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="mt-1 w-full flex items-center justify-between rounded border border-gray-300 bg-white px-3 py-[7px] text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
      >
        <span className="truncate">{displayText}</span>
        <svg
          className={`ml-2 h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-[9999] mt-1 max-h-60 w-auto min-w-[200px] overflow-auto rounded border border-gray-200 bg-white shadow-lg">
          {/* All option */}
          <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={handleAllToggle}
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm font-medium text-gray-700">All</span>
          </label>

          {showSearch && (
            <div className="sticky top-0 bg-white px-2 py-2 border-b border-gray-100">
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />
            </div>
          )}

          {shownOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
          )}

          {shownOptions.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={isChecked(opt.value)}
                onChange={() => handleToggle(opt.value)}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default CheckboxDropdown;
